/**
 * Training copy only (site built with REACT_APP_FL_TRAINING=1):
 * - TrainingLayer: the "practice copy" strip and the coach panel, in every shell;
 * - ScenarioCard: the practice situation for a delivery, pick or return.
 * The coach's advice arrives as `fl-coach` events from api.js.
 */
import React, { useEffect, useState } from 'react';
import { ArrowSquareOut, BookOpen, ChalkboardTeacher, HandPalm, Lightbulb, X } from '@phosphor-icons/react';
import { training } from './api';

export const TRAINING_BUILD = process.env.REACT_APP_FL_TRAINING === '1';
/** The real site, opened from the training copy. */
export const REAL_SITE_URL = (process.env.REACT_APP_FL_REAL_SITE_URL || 'https://shipdesi.com').replace(/\/$/, '');
/** The training copy, opened from the real Ops menu (link hidden when unset). */
export const TRAINING_SITE_URL = (process.env.REACT_APP_FL_TRAINING_URL || '').replace(/\/$/, '');

/** In the training copy, a link to `path` on the real site (new tab); elsewhere null. */
export function realSiteHref(path) {
  return TRAINING_BUILD ? `${REAL_SITE_URL}${path}` : null;
}
const LANG_KEY = 'fl.training.lang';

let statusPromise = null;
function loadStatus() {
  if (!TRAINING_BUILD) return Promise.resolve(null);
  if (!statusPromise) statusPromise = training.status().catch(() => { statusPromise = null; return null; });
  return statusPromise;
}

/** The training copy's status, or null (not a training copy). */
export function useTraining() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let live = true;
    loadStatus().then((s) => { if (live) setSt(s); });
    return () => { live = false; };
  }, []);
  return st;
}

function useLang() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) || 'en'; } catch { return 'en'; }
  });
  useEffect(() => {
    const on = () => { try { setLang(localStorage.getItem(LANG_KEY) || 'en'); } catch { /* ignore */ } };
    window.addEventListener('fl-training-lang', on);
    return () => window.removeEventListener('fl-training-lang', on);
  }, []);
  const set = (v) => {
    try { localStorage.setItem(LANG_KEY, v); } catch { /* ignore */ }
    setLang(v);
    window.dispatchEvent(new Event('fl-training-lang'));
  };
  return [lang, set];
}

function LangToggle({ lang, setLang, dark }) {
  const base = dark ? 'border-white/30' : 'border-[#8A5A00]/30';
  return (
    <div className={`inline-flex overflow-hidden rounded-full border text-[11px] font-bold ${base}`} role="group" aria-label="Language">
      {[['en', 'English'], ['ml', 'മലയാളം']].map(([k, label]) => (
        <button key={k} type="button" onClick={() => setLang(k)} aria-pressed={lang === k}
          className={`px-2.5 py-0.5 ${lang === k ? (dark ? 'bg-white text-[#1E1B19]' : 'bg-[#8A5A00] text-white') : ''}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Strip({ floor }) {
  const [lang, setLang] = useLang();
  return (
    <div data-testid="fl-training-strip"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs font-semibold sm:px-7 ${floor ? 'bg-[#FFD38A] text-[#1E1B19]' : 'mb-4 rounded-xl border border-[#E8C46A] bg-[#FFF4D6] text-[#5C3D00] dark:border-[#7A5A12] dark:bg-[#2A2110] dark:text-[#F5DFA8]'}`}>
      <span className="inline-flex items-center gap-1.5">
        <ChalkboardTeacher size={16} weight="bold" aria-hidden />
        {lang === 'ml' ? 'പരിശീലന പകർപ്പ് — ഇവിടെ ഒന്നും യഥാർത്ഥമല്ല' : 'TRAINING COPY — practice data, nothing here is real'}
      </span>
      <span className="hidden sm:inline opacity-80">
        {lang === 'ml' ? 'തെറ്റുകൾ കോച്ച് വിശദീകരിക്കും.' : 'The coach explains mistakes as you work.'}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <a href={REAL_SITE_URL} target="_blank" rel="noopener noreferrer" data-testid="fl-training-real"
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${floor ? 'bg-[#1E1B19] text-white' : 'bg-[#8A5A00] text-white'}`}>
          {lang === 'ml' ? 'യഥാർത്ഥ ShipDesi' : 'Open real ShipDesi'} <ArrowSquareOut size={12} weight="bold" aria-hidden />
        </a>
        <LangToggle lang={lang} setLang={setLang} />
      </span>
    </div>
  );
}

function CoachItem({ it, lang, onClose }) {
  const t = it[lang] || it.en;
  const stop = it.severity === 'stop';
  return (
    <div role={stop ? 'alert' : 'status'} data-testid={`fl-coach-${it.rule}`}
      className={`rounded-2xl border-2 bg-ship-card p-4 text-sm shadow-xl ${stop ? 'border-[#D94545]' : 'border-[#E0A82E]'}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${stop ? 'bg-[#D94545] text-white' : 'bg-[#FFE7A8] text-[#7A5200]'}`}>
          {stop ? <HandPalm size={16} weight="bold" /> : <Lightbulb size={16} weight="bold" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] font-bold uppercase tracking-[0.12em] ${stop ? 'text-[#B83A3A]' : 'text-[#8A5A00]'}`}>
            {stop ? (lang === 'ml' ? 'നിർത്തി — ഇത് ശരിയായ രീതിയല്ല' : 'Stopped — not the right method')
              : (lang === 'ml' ? 'ശ്രദ്ധിക്കുക' : 'Coach tip — you can carry on')}
          </div>
          <div className="mt-0.5 font-heading text-[15px] font-bold text-ship-text">{t.title}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-lg text-ship-muted hover:text-ship-text">
          <X size={16} />
        </button>
      </div>
      <p className="mt-2 text-ship-text">{t.why}</p>
      {t.steps?.length ? (
        <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-ship-text">
          {t.steps.map((s) => <li key={s}>{s}</li>)}
        </ol>
      ) : null}
      {it.detail ? <p className="mt-2 rounded-lg bg-ship-hover px-2 py-1 text-xs text-ship-muted">{it.detail}</p> : null}
      {it.manual ? (
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-ship-muted">
          <BookOpen size={14} aria-hidden /> {lang === 'ml' ? 'മാനുവൽ' : 'Manual'}: {it.manual}
        </div>
      ) : null}
    </div>
  );
}

function CoachPanel({ floor }) {
  const [items, setItems] = useState([]);
  const [lang] = useLang();
  useEffect(() => {
    const on = (e) => setItems((prev) => {
      const next = [...e.detail.map((x) => ({ ...x, key: `${x.rule}-${Date.now()}-${Math.random()}` })), ...prev];
      return next.slice(0, 3);
    });
    window.addEventListener('fl-coach', on);
    return () => window.removeEventListener('fl-coach', on);
  }, []);
  if (!items.length) return null;
  return (
    <div data-testid="fl-coach" aria-live="polite"
      className={`fixed right-3 z-50 flex w-[min(420px,calc(100vw-24px))] flex-col gap-2 ${floor ? 'bottom-28' : 'bottom-4'}`}>
      {items.map((it) => (
        <CoachItem key={it.key} it={it} lang={lang} onClose={() => setItems((p) => p.filter((x) => x.key !== it.key))} />
      ))}
    </div>
  );
}

/** Strip + coach; renders nothing outside the training copy. */
export function TrainingLayer({ floor = false }) {
  const st = useTraining();
  if (!st?.training) return null;
  return (
    <>
      <Strip floor={floor} />
      <CoachPanel floor={floor} />
    </>
  );
}

/** The practice situation for a delivery (asn), pick (task) or return (rma). */
export function ScenarioCard({ type, id }) {
  const st = useTraining();
  const [lang] = useLang();
  const [card, setCard] = useState(null);
  useEffect(() => {
    if (!st?.training || !id) return undefined;
    let live = true;
    training.scenario(type, id).then((r) => { if (live) setCard(r?.card ? r : null); }).catch(() => {});
    return () => { live = false; };
  }, [st, type, id]);
  if (!card) return null;
  return (
    <div data-testid="fl-scenario" className="mb-3 rounded-xl border-2 border-dashed border-[#4A7A8F] bg-[#EAF1F5] px-4 py-3 text-sm text-[#1E3A46] dark:bg-[#15242B] dark:text-[#CFE3EC]">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em]">
        <ChalkboardTeacher size={14} weight="bold" aria-hidden />
        {lang === 'ml' ? 'പരിശീലന സാഹചര്യം' : 'Practice situation'}
      </div>
      <p className="mt-1 font-semibold">{(lang === 'ml' && card.card_ml) || card.card}</p>
    </div>
  );
}
